import PropTypes from 'prop-types'
import React, { Component, createRef } from 'react'
import { Link } from 'react-router-dom'
import Address from '@ynap/address'
import { AddressSelectionFormWithRouter as AddressSelectionForm } from '@ynap/address-form-v1'
import { Step } from '@ynap/multi-step-form'
import BEMHelper from '@ynap/bem-helper'
import ChangeCountry from '../ChangeCountry/ChangeCountry'
import ShippingSummary from '../ShippingSummary/ShippingSummary'
import { Notification } from '@ynap/notification'
import createAnalyticsEvent, {
    addNewShippingAddress,
    confirmNewShippingAddress,
    shippingAddressContinueError,
    shippingAddressUpdate,
    shippingAddressEdit,
    shippingAddressContinue,
    addressHasShippingRestriction,
} from '../analytics'
import { getCrisisNotification } from '../../utils/crisis'
import { getJITNotification } from '../../utils/jit'
import { MetaData } from '../../utils/getMetaData'
import track from 'react-tracking'
import querystring from 'query-string'
import { scrollIntoView } from '@ynap/scroll-utils'
import { withOptimizely } from '@ynap/optimizely-utils'
import { checkIfUndefinedAsString } from 'app/utils/applePaySession'
import { cleanAddressEmptyValues } from 'app/utils/formatting'

const bem = new BEMHelper('ShippingAddressStep')

@track()
class ShippingAddressStep extends Component {
    static propTypes = {
        path: PropTypes.string.isRequired,
        optionsPath: PropTypes.string.isRequired,
        packagingPath: PropTypes.string.isRequired,
        config: PropTypes.object,
        nextPath: PropTypes.string,
        locale: PropTypes.object.isRequired,
        addresses: PropTypes.array,
        currentAddress: PropTypes.object,
        setShippingAddress: PropTypes.func.isRequired,
        disabled: PropTypes.bool,
        completed: PropTypes.bool,
        onChange: PropTypes.func.isRequired,
        messages: PropTypes.object.isRequired,
        editAddress: PropTypes.func.isRequired,
        fetchCart: PropTypes.func.isRequired,
        isSelectedAddressValid: PropTypes.bool,
        headerElement: PropTypes.element,
        hasShippingMethodChanged: PropTypes.object,
        userStatus: PropTypes.string,
    }

    constructor(props) {
        super(props)
        this.shippingOptionsRef = createRef()
    }

    state = {
        provincesFetched: false,
    }

    componentDidMount() {
        const { addresses, fetchCart, headerElement } = this.props
        const { clearMessages } = querystring.parse(this.props.location?.search, { arrayFormat: 'comma' })
        if (!addresses) {
            fetchCart({ clearMessages })
        }
        scrollIntoView(this.shippingOptionsRef, headerElement, 'smooth', true)
    }

    componentDidUpdate(prevProps) {
        const { fetchProvinces, countries, locale, hasShippingMethodChanged, headerElement, isFetchingProvinces } = this.props
        const { country } = locale
        const { provincesFetched } = this.state

        if (countries && Object.keys(countries).length > 0 && !countries[`${country}`]?.provinces && !isFetchingProvinces && !provincesFetched) {
            fetchProvinces(country)
            this.setState({ provincesFetched: true })
        }

        if (prevProps.hasShippingMethodChanged !== hasShippingMethodChanged && hasShippingMethodChanged) {
            scrollIntoView(this.shippingOptionsRef, headerElement, 'smooth', true)
        }
    }

    @track((props, state, metrics) => addressHasShippingRestriction(props, metrics[0]))
    addressShippingRestricted(metrics) {}

    @track(shippingAddressContinue)
    setShippingAddressContinue() {}

    @track(shippingAddressContinueError)
    setShippingAddressError() {}

    @track(addNewShippingAddress)
    addNewAddress() {}

    @track(confirmNewShippingAddress)
    confirmAddNewAddress() {}

    @track(shippingAddressEdit)
    editShippingAddress() {}

    @track(shippingAddressUpdate)
    updateShippingAddress() {}

    @track(({ checkoutAnalytics = {} }) =>
        createAnalyticsEvent({
            ...checkoutAnalytics,
            pageConfigs: {
                pageName: 'checkout - shipping address',
                pageType: 'checkout',
                primaryCategory: 'purchase',
                subCategory1: 'checkout',
                subCategory2: 'shipping address',
            },
        })
    )
    trackPage() {}

    renderForm(props) {
        const {
            addresses,
            config,
            messages,
            setShippingAddress,
            editAddress,
            addAddress,
            currentAddress,
            nextPath,
            optionsPath,
            packagingPath,
            paymentPath,
            analytics = {},
            shippingRestricted,
            countries,
            locale = {},
            headerElement,
            tracking,
            isSelectedAddressValid,
            handleStepClick,
            checkoutAnalytics,
            userStatus,
        } = props
        const { country } = locale
        const initialAddressType = 'ShippingAndBilling'

        let initialValues
        if (checkIfUndefinedAsString(currentAddress)) {
            initialValues = {
                ...currentAddress,
                firstName: undefined,
                lastName: undefined,
                addressLine: undefined,
                addressId: undefined,
                addressType: initialAddressType,
            }
        } else if (currentAddress && addresses && addresses.length) {
            initialValues = {
                addressId: currentAddress.addressId,
            }
        } else if (currentAddress) {
            initialValues = {
                ...currentAddress,
                addressId: undefined,
                addressType: initialAddressType,
            }
        } else {
            initialValues = {
                country,
                addressType: initialAddressType,
            }
        }
        const addAddressWithTransient = addAddress
            ? (address) => {
                  return new Promise((resolve) => resolve(window.SF?.getUserSummary())).then((user) => addAddress({ ...address, isTransient: !user }))
              }
            : addAddress

        return (
            <>
                {/* TODO: temporarily use deviceType to disable change country trigger for native apps until proper country change solution for webview is agreed */}
                <ChangeCountry {...locale} messages={messages} countryName={countries[country]?.countryName} deviceType={analytics.deviceType} />
                <AddressSelectionForm
                    form="shippingAddress"
                    onSubmit={async (values) => {
                        // Because this error is handled by Redux Form, we have to detect it here for reporting in analytics
                        try {
                            handleStepClick()
                            return await setShippingAddress(
                                cleanAddressEmptyValues(values),
                                nextPath,
                                packagingPath.replace(':locale', locale.id),
                                paymentPath.replace(':locale', locale.id)
                            )
                        } catch (err) {
                            this.setShippingAddressError()
                            throw err
                        }
                    }}
                    config={config}
                    editTitle={messages.shippingAddressForm.edit()}
                    addTitle={messages.shippingAddressForm.add()}
                    initialValues={initialValues}
                    editAddress={editAddress}
                    addAddress={addAddressWithTransient}
                    addresses={!checkIfUndefinedAsString(currentAddress) ? addresses : []}
                    hideCountry={true}
                    messages={messages}
                    trackAddNewAddress={this.addNewAddress.bind(this)}
                    trackConfirmAddNewAddress={this.confirmAddNewAddress.bind(this)}
                    trackEditAddress={this.editShippingAddress.bind(this)}
                    trackUpdateAddress={this.updateShippingAddress.bind(this)}
                    trackAddressShippingRestricted={this.addressShippingRestricted.bind(this)}
                    trackOnSubmitButtonClick={this.setShippingAddressContinue.bind(this)}
                    shippingRestricted={shippingRestricted}
                    enableReinitialize
                    keepDirtyOnReinitialize
                    locale={locale}
                    headerElement={headerElement}
                    tracking={tracking}
                    isSelectedAddressValid={isSelectedAddressValid}
                    primaryCategory="checkout"
                    checkoutAnalytics={checkoutAnalytics}
                    isGuestUser={userStatus !== 'R'}
                />
            </>
        )
    }

    renderSummary(props) {
        const {
            currentAddress,
            messages,
            path,
            optionsPath,
            packagingPath,
            locale,
            currentShippingOption,
            currentShippingOptionJit,
            currentOrderShippingInfo,
            currentOrderShippingInfoJit,
            crisisMessages,
            hasJITItems,
            currentPackagingOption,
            basicOnly,
            isGift,
            jitMessages,
            hasShippingMethodChanged,
            isPostSplit,
            optimizely,
        } = props
        const optionsSummary = props.location?.pathname?.includes('/checkout/shipping-options') ? null : (
            <ShippingSummary
                className={bem('summaryOptions')}
                shippingOption={currentShippingOption}
                shippingOptionJit={currentShippingOptionJit}
                shippingOptionInfo={currentOrderShippingInfo}
                shippingOptionInfoJit={currentOrderShippingInfoJit}
                isPostSplit={isPostSplit}
            />
        )
        const crisisMessage = !!optionsSummary && getCrisisNotification(bem, false, crisisMessages)
        const jitMessage = optionsSummary && hasJITItems && !isPostSplit ? getJITNotification(jitMessages, 'shippingStepA', bem) : null
        const packagingOptionKey = currentPackagingOption?.key
        const packagingLabel =
            packagingOptionKey && messages.packagingOptions?.label
                ? messages.packagingOptions.label?.[packagingOptionKey]?.()
                : currentPackagingOption?.description

        const packagingSummary =
            props.location?.pathname?.includes('/checkout/shipping-options') || props.location?.pathname?.includes('/checkout/packaging-options') ? null : (
                <>
                    {basicOnly && messages.notifications?.packagingInfo?.basicOnly && (
                        <Notification className={bem('basic')} message={messages.notifications.packagingInfo.basicOnly()} />
                    )}
                    <ul className={bem('summaryPackaging')}>
                        <li>{packagingLabel}</li>
                        {isGift === 'Y' && <li>{messages.packagingOptions?.addGiftSummaryLabel()}</li>}
                    </ul>
                </>
            )

        return (
            <div className={bem('wrapper')} ref={this.shippingOptionsRef}>
                <div className={bem('content')}>
                    <div className={bem('title')}>{messages.shippingAddressForm?.shipTo?.()}</div>
                    <Address {...currentAddress} />

                    <Link className={bem('changeLink')} to={path.replace(':locale', locale.id)}>
                        {messages.purchasePath.edit()}
                    </Link>
                </div>
                {!!optionsSummary && (
                    <div className={bem('content')}>
                        <div className={bem('title')}>{messages.shippingAddressForm?.optionsTitle?.()}</div>
                        {optionsSummary}
                        {hasShippingMethodChanged && (
                            <div className={bem('shippingChangedNotification')}>{messages.shippingOptionsForm.shippingOptionChanged()}</div>
                        )}

                        <Link
                            className={bem('changeLink')}
                            to={optionsPath?.replace(':locale', locale.id)}
                            onClick={() => optimizely?.track('change_shipping_option')}
                        >
                            {messages.purchasePath.edit()}
                        </Link>
                    </div>
                )}
                {(!!crisisMessage || !!jitMessage) && (
                    <div className={bem('content', 'full')}>
                        {crisisMessage}
                        {jitMessage}
                    </div>
                )}
                {!!packagingSummary && (
                    <div className={bem('content')}>
                        <div className={bem('title')}>{messages.shippingAddressForm?.packagingTitle?.()}</div>
                        {packagingSummary}

                        <Link className={bem('changeLink')} to={packagingPath?.replace(':locale', locale.id)}>
                            {messages.purchasePath.edit()}
                        </Link>
                    </div>
                )}
            </div>
        )
    }

    render() {
        const { locale, messages, config, ...rest } = this.props
        return (
            <>
                <MetaData title={messages.shippingAddressForm.addressTitle()} brandId={config.global.application.brandId} />

                <Step
                    title={messages.shippingAddressForm.addressTitle()}
                    form={this.renderForm.bind(this)}
                    summary={this.renderSummary.bind(this)}
                    className={bem('step')}
                    locale={locale}
                    messages={messages}
                    pageAnalyticsStep="shipping address"
                    pageAnalyticsFn={this.trackPage.bind(this)}
                    config={config}
                    isCheckoutLayout={true}
                    {...rest}
                />
            </>
        )
    }
}

export default withOptimizely(ShippingAddressStep)
